import {SchemaMask, SelectorResult} from './model';

/**
 * Helpers for a field's extraction chain: its list of steps (selector groups and schema steps), run in
 * manual_priority order (then timestamp, newest first) exactly like AutoScraper's _sorted_by_priority.
 */

/** Returns the steps in the order extraction tries them; the input list is not changed. */
export function chainOrder(steps: SelectorResult[]): SelectorResult[] {
  return [...steps].sort((a, b) =>
    (b.manual_priority ?? 0) - (a.manual_priority ?? 0) || (b.timestamp ?? '').localeCompare(a.timestamp ?? ''));
}

/** Returns 'Schema', 'XPath' or 'CSS' for display. */
export function stepKind(step: SelectorResult): 'Schema' | 'XPath' | 'CSS' {
  if (step.is_schema) return 'Schema';
  return step.is_xpath ? 'XPath' : 'CSS';
}

/** Reports whether the list contains an enabled schema step. */
export function hasSchemaStep(steps: SelectorResult[] | null | undefined): boolean {
  return (steps ?? []).some(s => s.is_schema && s.enabled !== false);
}

/** Returns a new schema step that runs before every existing step. */
export function newSchemaStep(steps: SelectorResult[]): SelectorResult {
  const top = Math.max(-1, ...steps.map(s => s.manual_priority ?? 0)) + 1;
  return {
    target_value: null,
    extracted_value: null,
    selector: [],
    is_xpath: false,
    value_to_extract: null,
    manual_priority: top,
    timestamp: new Date().toISOString().slice(0, 16),
    post_processing: [],
    is_schema: true,
    schema_paths: [],
    enabled: true,
  };
}

/** Returns the list with a schema step first (added if missing), leaving the selector steps as fall-through. */
export function withSchemaStep(steps: SelectorResult[]): SelectorResult[] {
  return hasSchemaStep(steps) ? steps : [newSchemaStep(steps), ...steps.filter(s => !s.is_schema)];
}

/** Returns the list without schema steps. */
export function withoutSchemaSteps(steps: SelectorResult[]): SelectorResult[] {
  return steps.filter(s => !s.is_schema);
}

/**
 * Moves a step one position up (-1) or down (+1) in the chain by rewriting manual_priority of all steps,
 * so the stored order matches what the user sees.
 */
export function moveStep(steps: SelectorResult[], step: SelectorResult, direction: -1 | 1): void {
  const ordered = chainOrder(steps);
  const from = ordered.indexOf(step);
  const to = from + direction;
  if (from < 0 || to < 0 || to >= ordered.length) return;
  [ordered[from], ordered[to]] = [ordered[to], ordered[from]];
  ordered.forEach((s, i) => { s.manual_priority = ordered.length - 1 - i; });
}

/** Field path per legacy use_schema key, as AutoScraper names fields for the default schema lookup. */
export const SCHEMA_KEY_FIELD: Record<keyof SchemaMask, string> = {
  oem_info_brand: 'oem_info.brand',
  oem_info_number: 'oem_info.number',
  oem_info_gtin: 'oem_info.gtin',
  internal_info_number: 'internal_info.number',
  internal_info_name: 'internal_info.name',
  internal_info_description: 'internal_info.description',
  internal_info_group: 'internal_info.group',
  prices_price: 'prices.price',
  prices_currency: 'prices.currency_text',
  prices_vat: 'prices.vat_text',
};

/** Escapes text for the overlays that are built as HTML strings. */
export function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
